import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DeviceCode, DeviceCodeStatus, Flow } from '@myrtls/api-interfaces';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    filter,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import {
    FLOWS_ENDPOINT,
    SITES_API_URL,
    SNACK_BAR_DURATION,
} from '../../shared/utils/constants';
import { AppState } from '../index';
import { showSnackBarAction } from '../snack-bar/snack-bar.actions';
import { $selectedSiteId } from '../sites/sites.selectors';
import { SnackBarType } from '../../shared/models/snack-bar-config.model';
import { interval, of } from 'rxjs';
import {
    createFlowAction,
    createFlowFailureAction,
    createFlowSuccessAction,
    deleteDeviceAction,
    deleteDeviceFailureAction,
    deleteDeviceSuccessAction,
    deleteFlowAction,
    deleteFlowFailureAction,
    deleteFlowSuccessAction,
    generateFlowDeviceCodeAction,
    generateFlowDeviceCodeFailureAction,
    generateFlowDeviceCodeSuccessAction,
    getCodeStatusAction,
    getCodeStatusFailureAction,
    getCodeStatusSuccessAction,
    getFlowsAction,
    getFlowsFailureAction,
    getFlowsSuccessAction,
    startPollingCodeStatusAction,
    stopPollingCodeStatusAction,
    updateDeviceNoteAction,
    updateDeviceNoteFailureAction,
    updateDeviceNoteSuccessAction,
    updateFlowAction,
    updateFlowFailureAction,
    updateFlowSuccessAction,
} from './flows.actions';

@Injectable()
export class FlowsEffects {
    getFlows$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getFlowsAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) =>
                this.http
                    .get<Flow[]>(`${SITES_API_URL}/${siteId}/${FLOWS_ENDPOINT}`)
                    .pipe(
                        map(flows => getFlowsSuccessAction({ flows })),
                        catchError(error =>
                            of(getFlowsFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    createFlow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createFlowAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ flow }, siteId]) =>
                this.http
                    .post(`${SITES_API_URL}/${siteId}/${FLOWS_ENDPOINT}`, flow)
                    .pipe(
                        mergeMap(() => [
                            createFlowSuccessAction(),
                            getFlowsAction(),
                            showSnackBarAction({
                                config: {
                                    type: SnackBarType.SUCCESSFUL,
                                    description: 'Flow created successfully!',
                                    duration: SNACK_BAR_DURATION,
                                },
                            }),
                        ]),
                        catchError(error =>
                            of(createFlowFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    updateFlow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateFlowAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ flow }, siteId]) =>
                this.http
                    .put(
                        `${SITES_API_URL}/${siteId}/${FLOWS_ENDPOINT}/${flow.id}`,
                        flow,
                    )
                    .pipe(
                        mergeMap(() => [
                            updateFlowSuccessAction(),
                            getFlowsAction(),
                            showSnackBarAction({
                                config: {
                                    type: SnackBarType.SUCCESSFUL,
                                    description: 'Flow updated successfully!',
                                    duration: SNACK_BAR_DURATION,
                                },
                            }),
                        ]),
                        catchError(error =>
                            of(updateFlowFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    deleteFlow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteFlowAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ flowId }, siteId]) =>
                this.http
                    .delete(
                        `${SITES_API_URL}/${siteId}/${FLOWS_ENDPOINT}/${flowId}`,
                    )
                    .pipe(
                        mergeMap(() => [
                            deleteFlowSuccessAction(),
                            getFlowsAction(),
                            showSnackBarAction({
                                config: {
                                    type: SnackBarType.SUCCESSFUL,
                                    description: 'Flow deleted successfully!',
                                    duration: SNACK_BAR_DURATION,
                                },
                            }),
                        ]),
                        catchError(error =>
                            of(deleteFlowFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    generateFlowDeviceCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(generateFlowDeviceCodeAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ flowId }, siteId]) =>
                this.http
                    .post<DeviceCode>(
                        `${SITES_API_URL}/${siteId}/${FLOWS_ENDPOINT}/${flowId}/generate`,
                        {},
                    )
                    .pipe(
                        mergeMap(code => [
                            generateFlowDeviceCodeSuccessAction(code),
                            startPollingCodeStatusAction({
                                code: code.code,
                                intervalTime: 10000,
                            }),
                        ]),
                        catchError(error =>
                            of(generateFlowDeviceCodeFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    startPollingCodeStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(startPollingCodeStatusAction),
            switchMap(({ code, intervalTime }) =>
                interval(intervalTime).pipe(
                    takeUntil(
                        this.actions$.pipe(ofType(stopPollingCodeStatusAction)),
                    ),
                    map(() => getCodeStatusAction({ code })),
                ),
            ),
        ),
    );

    getCodeStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getCodeStatusAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ code }, siteId]) =>
                this.http
                    .get<DeviceCodeStatus>(
                        `${SITES_API_URL}/${siteId}/${FLOWS_ENDPOINT}/status/${code}`,
                    )
                    .pipe(
                        map(response => getCodeStatusSuccessAction(response)),
                        catchError(error =>
                            of(getCodeStatusFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    refreshFlowsOnDevicePaired$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getCodeStatusSuccessAction),
            filter(status => status.status === 'used'),
            map(() => getFlowsAction()),
        ),
    );

    stopPollingOnStatusUpdate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getCodeStatusSuccessAction),
            filter(
                status =>
                    status.status === 'used' || status.status === 'invalid',
            ),
            map(() => stopPollingCodeStatusAction()),
        ),
    );

    updateDeviceNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateDeviceNoteAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ deviceId, note }, siteId]) =>
                this.http
                    .patch(
                        `${SITES_API_URL}/${siteId}/devices/${deviceId}/note`,
                        { note },
                    )
                    .pipe(
                        mergeMap(() => [
                            updateDeviceNoteSuccessAction(),
                            getFlowsAction(),
                        ]),
                        catchError(error =>
                            of(updateDeviceNoteFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    deleteDevice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteDeviceAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ deviceId }, siteId]) =>
                this.http
                    .delete(`${SITES_API_URL}/${siteId}/devices/${deviceId}`)
                    .pipe(
                        mergeMap(() => [
                            deleteDeviceSuccessAction(),
                            getFlowsAction(),
                            showSnackBarAction({
                                config: {
                                    type: SnackBarType.SUCCESSFUL,
                                    description: 'Device deleted successfully!',
                                    duration: SNACK_BAR_DURATION,
                                },
                            }),
                        ]),
                        catchError(error =>
                            of(deleteDeviceFailureAction({ error })),
                        ),
                    ),
            ),
        ),
    );

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store<AppState>,
        private readonly http: HttpClient,
    ) {}
}
